import { useState, useEffect, useRef } from 'react';
import { MdSend, MdMic, MdStop } from 'react-icons/md';
import { TbLayoutSidebarRightExpand, TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  LiveAvatarSession,
  SessionState,
  SessionEvent,
  AgentEventsEnum,
} from '@heygen/liveavatar-web-sdk';
import { useAdvisor } from '../context/AdvisorContext';

// ── Inline chart payload renderer ────────────────────────────────────────────
// Receives the structured `chart` object the agent attaches to its
// respond_to_client tool call. Schema:
//   { chartType: 'line'|'bar'|'area', title?, xLabel?, yLabel?,
//     series: [{ name, color?, data: [{x, y}] }] }
// Dark-theme inline-chart palette: mint as the lead colour, then warm/cool
// accents for additional series. Picked to read clearly against the
// near-black chat-bubble background.
const CHART_PALETTE = ['#00ff88', '#ffb020', '#ff5b5b', '#a78bfa', '#22d3ee', '#f59e0b', '#ec4899'];
const chartTooltipStyle = { backgroundColor: '#1c2230', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#e6e8eb', fontSize: 12 };

function MessageChart({ chart }) {
  if (!chart || !Array.isArray(chart.series) || chart.series.length === 0) return null;
  const { chartType = 'line', title, xLabel, yLabel, series } = chart;

  // Pivot {series:[{name,data:[{x,y}]}]} → recharts row form {x, [seriesName]: y, ...}
  const xSet = new Set();
  for (const s of series) for (const p of (s.data || [])) xSet.add(p.x);
  const xValues = [...xSet].sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  });
  const data = xValues.map(x => {
    const row = { x };
    for (const s of series) {
      const p = (s.data || []).find(p => p.x === x);
      row[s.name] = p ? p.y : null;
    }
    return row;
  });

  const ChartCmp = chartType === 'bar' ? BarChart : chartType === 'area' ? AreaChart : LineChart;
  const SeriesCmp = chartType === 'bar' ? Bar : chartType === 'area' ? Area : Line;

  // Pad the Y axis 5% outward from the data range. The literal "95% of min /
  // 105% of max" rule only works correctly when values are positive — for
  // negative values we flip the multiplier so the bound stays outside the
  // data point. Single-value series get a flat ±5% pad off zero.
  const allY = [];
  for (const s of series) for (const p of (s.data || [])) {
    if (typeof p.y === 'number' && Number.isFinite(p.y)) allY.push(p.y);
  }
  let yDomain;
  if (allY.length > 0) {
    const yMin = Math.min(...allY);
    const yMax = Math.max(...allY);
    if (yMin === yMax) {
      const pad = Math.abs(yMin) * 0.05 || 1;
      yDomain = [yMin - pad, yMax + pad];
    } else {
      yDomain = [
        yMin >= 0 ? yMin * 0.95 : yMin * 1.05,
        yMax >= 0 ? yMax * 1.05 : yMax * 0.95,
      ];
    }
  }

  // Per-series colour: ignore agent-supplied `color` so the app palette is
  // always applied — keeps inline charts visually consistent with the
  // Strategy backtest chart (mint lead, neutral grey 2nd, etc.) regardless
  // of what Claude emits in the tool call. The series get the same vertical
  // gradient fill treatment as the backtest chart.
  const seriesColors = series.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]);
  // Stable-ish gradient ids per render so multiple charts don't collide.
  const gradPrefix = `msgGrad_${Math.abs(((title || '') + series.length).split('').reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0))}_`;

  return (
    <div className="msg-chart">
      {title && <div className="msg-chart__title">{title}</div>}
      <ResponsiveContainer width="100%" height={200}>
        <ChartCmp data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          {(chartType === 'area' || chartType === 'line') && (
            <defs>
              {seriesColors.map((c, i) => (
                <linearGradient key={i} id={`${gradPrefix}${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={c} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={c} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis
            dataKey="x"
            tick={{ fontSize: 10, fill: '#5a6068' }}
            tickLine={false}
            label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -4, fontSize: 10, fill: '#5a6068' } : undefined}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#5a6068' }}
            tickLine={false}
            axisLine={false}
            width={42}
            domain={yDomain}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 10, fill: '#5a6068' } : undefined}
          />
          <Tooltip contentStyle={chartTooltipStyle} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: '#8a9099' }} />}
          {series.map((s, i) => {
            const color = seriesColors[i];
            const isPrimary = i === 0;
            let props;
            if (chartType === 'bar') {
              props = { fill: color };
            } else if (chartType === 'area') {
              props = {
                stroke: color,
                strokeWidth: 2.2,
                dot: false,
                fill: `url(#${gradPrefix}${i})`,
                strokeDasharray: isPrimary ? undefined : '4 2',
              };
            } else {
              // line — match backtest chart's "primary solid + secondary dashed"
              // treatment so the user instantly recognises the styling.
              props = {
                stroke: color,
                strokeWidth: isPrimary ? 2.2 : 1.5,
                dot: false,
                strokeDasharray: isPrimary ? undefined : '4 2',
              };
            }
            return <SeriesCmp key={s.name} type="monotone" dataKey={s.name} {...props} />;
          })}
        </ChartCmp>
      </ResponsiveContainer>
    </div>
  );
}

const STRATEGY_PROMPTS = [
  'Dial back risk and increase hedging',
  'I want more gold',
  "What's in this portfolio right now?",
];

const HOME_PROMPTS = [
  'How is this portfolio doing today?',
  'What would happen to my portfolio if Fed hiked rates?',
];

function TypingIndicator() {
  return (
    <div className="msg msg--ai">
      <div className="msg__bubble msg__bubble--typing"><span /><span /><span /></div>
    </div>
  );
}

// Strategy/Home avatar surface. Two states:
//   1. No chatLiveAvatarToken (mic idle, or token mint failed) — render the
//      onboarding-selected advisor's still image, with a mic button to start
//      voice. Identical to the pre-LiveAvatar look.
//   2. chatLiveAvatarToken present — mount a LiveAvatarSession (hybrid mode:
//      no Convai connector, voiceChat: false). The avatar's video stream
//      attaches to <video>, and Claude's responses lip-sync via the context's
//      session.repeat() path. The still image shows underneath until the
//      stream is live, masking the connection delay.
function AvatarVoiceButton({ phase, onClick, previewUrl, advisorName }) {
  const ctx = useAdvisor();
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const token = ctx.chatLiveAvatarToken;
  const videoRef = useRef(null);
  const sessionRef = useRef(null);
  const [sessionState,   setSessionState]   = useState(SessionState.INACTIVE);
  const [streamReady,    setStreamReady]    = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);

  // One-shot session lifecycle: create on token change, tear down on cleanup
  // (stopVoice clears the token, which unmounts the session).
  useEffect(() => {
    if (!token) {
      setSessionState(SessionState.INACTIVE);
      setStreamReady(false);
      setIsAvatarTalking(false);
      return;
    }
    let disposed = false;
    const session = new LiveAvatarSession(token, { voiceChat: false });
    sessionRef.current = session;
    ctxRef.current.registerChatAvatar?.(session);

    session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
      if (disposed) return;
      console.log('[chat-avatar] session.state', state);
      setSessionState(state);
      if (state === SessionState.DISCONNECTED) {
        ctxRef.current.notifyChatAvatarReady?.(false);
      }
    });
    session.on(SessionEvent.SESSION_STREAM_READY, () => {
      if (disposed) return;
      console.log('[chat-avatar] stream.ready');
      setStreamReady(true);
      const v = videoRef.current;
      if (v) {
        try { session.attach(v); } catch (e) { console.warn('[chat-avatar] attach failed:', e); }
        const tryPlay = () => v.play().catch((e) => console.warn('[chat-avatar] video.play failed:', e));
        if (v.readyState >= 1) tryPlay();
        else v.addEventListener('loadedmetadata', tryPlay, { once: true });
      }
      ctxRef.current.notifyChatAvatarReady?.(true);
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      console.log('[chat-avatar] avatar.speak_started');
      if (disposed) return;
      setIsAvatarTalking(true);
      // Unblocks speakText's startup-timeout race so we know HeyGen
      // actually accepted the repeat command (vs. silently dropping it).
      ctxRef.current.notifyChatAvatarSpeakStarted?.();
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      console.log('[chat-avatar] avatar.speak_ended');
      if (disposed) return;
      setIsAvatarTalking(false);
      // Drives the post-speech mic re-open in AdvisorContext.
      ctxRef.current.notifyChatAvatarSpeakEnded?.();
    });

    session.start().catch((e) => {
      console.warn('chat LiveAvatarSession.start failed:', e);
    });

    return () => {
      disposed = true;
      ctxRef.current.unregisterChatAvatar?.(session);
      try { session.removeAllListeners(); } catch {}
      try {
        const p = session.stop();
        if (p && typeof p.then === 'function') p.catch(() => {});
      } catch {}
      sessionRef.current = null;
    };
  }, [token]);

  // Race guard: if the video element mounts after STREAM_READY (e.g. initial
  // render order), attach when the ref resolves.
  useEffect(() => {
    if (streamReady && videoRef.current && sessionRef.current) {
      try { sessionRef.current.attach(videoRef.current); } catch {}
    }
  }, [streamReady]);

  const isActive     = phase === 'listening' || phase === 'speaking';
  const isSpeaking   = phase === 'speaking' || isAvatarTalking;
  const isConnecting = phase === 'connecting' || (!!token && sessionState !== SessionState.CONNECTED);
  const isLive       = sessionState === SessionState.CONNECTED && streamReady;

  const statusText =
    isConnecting ? 'Connecting…'
    : isSpeaking ? 'Speaking…'
    : phase === 'listening' ? 'Listening…'
    : 'Tap to start voice chat';

  return (
    <div className="voice-trigger voice-trigger--avatar">
      <div className={`chat-avatar-frame ${isSpeaking ? 'chat-avatar-frame--speaking' : isActive ? 'chat-avatar-frame--active' : ''}`}>
        {/* The <video> stays in the DOM whenever there's a token, so the SDK
            can attach when the stream becomes available. CSS hides it until
            the stream is live; the still shows through underneath. */}
        {token && (
          <video
            ref={videoRef}
            className={`chat-avatar-frame__video ${isLive ? 'chat-avatar-frame__video--live' : ''}`}
            autoPlay
            playsInline
            muted={false}
          />
        )}
        {(!token || !isLive) && (
          previewUrl
            ? <img src={previewUrl} alt={advisorName || ''} className="chat-avatar-frame__still" />
            : <div className="chat-avatar-frame__placeholder" />
        )}
        <button
          className={[
            'chat-avatar-mic',
            isActive && 'chat-avatar-mic--active',
            isSpeaking && 'chat-avatar-mic--speaking',
            isConnecting && 'chat-avatar-mic--processing',
          ].filter(Boolean).join(' ')}
          onClick={onClick}
          type="button"
          aria-label={isActive ? 'Stop voice chat' : 'Start voice chat'}
        >
          {isActive ? <MdStop size={20} /> : <MdMic size={20} />}
        </button>
      </div>
      <p className="voice-status">{statusText}</p>
    </div>
  );
}

export default function ChatPanel({ kind = 'strategy' }) {
  const ctx = useAdvisor();
  const isHome = kind === 'home';

  const messages   = isHome ? ctx.homeMessages : ctx.messages;
  const input      = isHome ? ctx.homeInput    : ctx.input;
  const setInput   = isHome ? ctx.setHomeInput : ctx.setInput;
  const phase      = isHome ? ctx.homePhase    : ctx.phase;
  const send       = isHome ? ctx.homeSend     : ctx.send;
  const handleKey  = isHome ? ctx.homeHandleKey : ctx.handleKey;
  const bottomRef  = isHome ? ctx.homeBottomRef : ctx.bottomRef;
  const inputRef   = isHome ? ctx.homeInputRef  : ctx.inputRef;
  const headerName = 'Portfolio Advisor';
  const placeholder = isHome
    ? 'Ask about your portfolio — e.g. "how are we doing?" or "sector mix now"…'
    : ctx.inputPlaceholder;

  // Strategy / Home use browser SpeechRecognition + ElevenLabs TTS; their
  // voiceState directly reflects the orb phase.
  const voiceActiveHere = ctx.voiceActivePage === kind;
  const orbPhase = voiceActiveHere ? ctx.voiceState : 'idle';

  function handleOrbClick() {
    if (voiceActiveHere) {
      ctx.stopVoice();
    } else {
      if (isHome) ctx.startHomeVoice(); else ctx.startStrategyVoice();
    }
  }

  // Focus-mode expansion (Home only) — chat grows over the dashboard for
  // analytical work; backdrop dim+blur the visible main-area sliver behind it.
  // Multiple ways out: header button, ESC key, click on the dim portion.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!isHome || !expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isHome, expanded]);

  return (
    <>
      {isHome && expanded && (
        <div
          className="chat-focus-backdrop"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}
      <div className={`chat-panel ${isHome && expanded ? 'chat-panel--expanded' : ''}`}>
        <div className="chat-header">
          <div className="chat-header__avatar">AI</div>
          <div className="chat-header__name-block">
            <div className="chat-header__name">{headerName}</div>
            <div className="chat-header__status">{phase === 'thinking' ? 'Thinking…' : 'Online'}</div>
          </div>
          {isHome && (
            <button
              className="chat-focus-toggle"
              onClick={() => setExpanded(e => !e)}
              aria-label={expanded ? 'Collapse focus mode' : 'Expand for focused research'}
              title={expanded ? 'Collapse (Esc)' : 'Expand for focused research'}
              type="button"
            >
              {expanded ? <TbLayoutSidebarRightCollapse size={20} /> : <TbLayoutSidebarRightExpand size={20} />}
            </button>
          )}
        </div>

      <AvatarVoiceButton
        phase={orbPhase}
        onClick={handleOrbClick}
        previewUrl={ctx.advisorPreviewUrl}
        advisorName={ctx.selectedAdvisor?.name}
      />
      <div className="chat-divider"><span>or type below</span></div>

      <div className="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`msg msg--${m.role}`}>
            <div className="msg__bubble">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                // Force every link to open in a new tab and never inject any
                // markdown HTML (we don't render raw HTML for safety).
                components={{
                  a: (props) => <a {...props} target="_blank" rel="noreferrer" />,
                }}
              >
                {m.text || ''}
              </ReactMarkdown>
              {m.chart && <MessageChart chart={m.chart} />}
            </div>
          </div>
        ))}
        {phase === 'thinking' && <TypingIndicator />}
        <div ref={bottomRef} />
      </div>

      {(() => {
        const engaged = isHome ? ctx.homeEngaged : ctx.strategyEngaged;
        const prompts = isHome ? HOME_PROMPTS    : STRATEGY_PROMPTS;
        if (engaged || phase === 'thinking') return null;
        const sendQuick = (text) => {
          if (isHome) ctx.homeSendDirect(text);
          else        ctx.sendDirect(text);
        };
        return (
          <div className="chat-suggestions">
            <div className="chat-suggestions__header">
              <span className="chat-suggestions__label">Suggested Follow-Ups</span>
            </div>
            {prompts.map((p) => (
              <button key={p} className="chat-suggestion" onClick={() => sendQuick(p)}>
                {p}
              </button>
            ))}
          </div>
        );
      })()}

      <div className="chat-input-row">
        <textarea
          ref={inputRef}
          className="chat-input"
          rows={2}
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={phase === 'thinking'}
        />
        <button className="send-btn" onClick={send} disabled={!input.trim() || phase === 'thinking'}>
          <MdSend size={20} />
        </button>
      </div>
      </div>
    </>
  );
}
